import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Plus, Search, Save, AlertCircle, CheckCircle, Trash2, LogOut, Shield, Activity, TrendingUp, Users, Camera, Image, Settings, User, Mail, Lock, Eye, EyeOff, X, Edit3, ChevronDown, ChevronRight, Building2, Menu } from 'lucide-react';
import PhotoManager from './PhotoManager';
import { useSchool } from '../contexts/SchoolContext';

// Richmond Street School Logo Component - Updated to accept dynamic logoUrl
const RichmondStreetSchoolLogo = ({ size = 48, className = "", logoUrl = "/assets/school-logo.png" }) => {
  return (
  <img
      src={logoUrl}
      alt="School Logo"
      width={size}
      height={size}
      className="rounded-full"
      />
  );
};

const TimeTracker = ({ user, token, onLogout, onViewChange }) => {
  // School Context
  const { schools, selectedSchoolId, selectedSchool, selectSchool } = useSchool();

  const [activities, setActivities] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [hours, setHours] = useState('');
  const [entryDate, setEntryDate] = useState(''); // Start with empty date
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [view, setView] = useState('activities'); // 'activities', 'recent', 'photos', or 'settings'
  const [selectedActivityForPhotos, setSelectedActivityForPhotos] = useState(null);
  const [photoStats, setPhotoStats] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);

  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsData, setSettingsData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    schoolIds: user?.schools?.map(s => s.id) || [],
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [availableSchools, setAvailableSchools] = useState([]);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    recent: true,
    events: false,
    yearRound: false,
    classroom: false
  });
  const [recentActivities, setRecentActivities] = useState([]);

  const apiCall = async (url, options = {}) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  };

  useEffect(() => {
    if (selectedSchoolId) {
      fetchActivities();
      fetchRecentTimeEntries();
    }
  }, [selectedSchoolId]);

  useEffect(() => {
    // Update settings data when user changes
    setSettingsData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      schoolIds: user?.schools?.map(s => s.id) || [],
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  }, [user]);

  useEffect(() => {
    // Fetch available schools when settings modal opens
    if (showSettings) {
      fetchAvailableSchools();
    }
  }, [showSettings]);

  const fetchAvailableSchools = async () => {
    try {
      const data = await apiCall('/api/schools/public');
      setAvailableSchools(data);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    }
  };

  const fetchActivities = async () => {
    if (!selectedSchoolId) return;

    try {
      const data = await apiCall(`/api/activities?schoolId=${selectedSchoolId}`);
      setActivities(data);

      // Fetch photo counts for each activity
      const photoStatsPromises = data.map(async (activity) => {
        try {
          const photoData = await apiCall(`/api/activities/${activity.id}/photos?limit=1&schoolId=${selectedSchoolId}`);
          return { [activity.id]: photoData.pagination.total };
        } catch (error) {
          return { [activity.id]: 0 };
        }
      });

      const photoStatsArray = await Promise.all(photoStatsPromises);
      const photoStatsObj = photoStatsArray.reduce((acc, stat) => ({ ...acc, ...stat }), {});
      setPhotoStats(photoStatsObj);
    } catch (error) {
      showMessage('error', 'Failed to load activities');
    }
  };

  const fetchRecentTimeEntries = async () => {
    if (!selectedSchoolId) return;

    try {
      const data = await apiCall(`/api/time-entries?limit=20&schoolId=${selectedSchoolId}`);
      setTimeEntries(data.timeEntries);
    } catch (error) {
      showMessage('error', 'Failed to load recent entries');
    }
  };

  // Split activities into classroom and other categories
  // Updated getActivityLists function to handle year-round activities
  const getActivityLists = () => {
    const allActivities = activities.filter(activity =>
      activity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.category.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Year-round activities: Classroom category OR activities without start dates
    const yearRoundActivities = allActivities.filter(activity => 
      activity.category.name.toLowerCase() === 'classroom' || !activity.activeFrom
    );

    const eventsActivities = allActivities.filter(activity => 
      activity.category.name.toLowerCase() === 'events'
    );

    // Other activities: have start dates AND are not classroom or events
    const otherActivities = allActivities.filter(activity => 
      activity.activeFrom && 
      activity.category.name.toLowerCase() !== 'classroom' && 
      activity.category.name.toLowerCase() !== 'events'
    );

    // Sort year-round activities by name (no date sorting needed)
    const sortedYearRoundActivities = yearRoundActivities.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

    // Sort events activities by activeFrom date (today first, then upcoming, then past)
    const sortedEventsActivities = eventsActivities.sort((a, b) => {
      const now = new Date();
      const todayUTC = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate()
      ));

      const dateA = a.activeFrom ? new Date(a.activeFrom) : null;
      const dateB = b.activeFrom ? new Date(b.activeFrom) : null;

      // Handle cases where one or both don't have dates
      if (!dateA && !dateB) return a.name.localeCompare(b.name);
      if (!dateA) return 1; // Put activities without dates at the end
      if (!dateB) return -1;

      // Normalize dates to UTC for comparison
      const dateAUTC = new Date(Date.UTC(
        dateA.getUTCFullYear(),
        dateA.getUTCMonth(),
        dateA.getUTCDate()
      ));
      const dateBUTC = new Date(Date.UTC(
        dateB.getUTCFullYear(),
        dateB.getUTCMonth(),
        dateB.getUTCDate()
      ));

      // Check if events are today
      const isAToday = dateAUTC.getTime() === todayUTC.getTime();
      const isBToday = dateBUTC.getTime() === todayUTC.getTime();

      // Today's events come first
      if (isAToday && !isBToday) return -1;
      if (!isAToday && isBToday) return 1;
      if (isAToday && isBToday) return a.name.localeCompare(b.name);

      // Categorize remaining events
      const isAFuture = dateAUTC > todayUTC;
      const isBFuture = dateBUTC > todayUTC;

      // If one is future and one is past, put future first
      if (isAFuture && !isBFuture) return -1;
      if (!isAFuture && isBFuture) return 1;

      // If both are future or both are past, sort by date (ascending for future, descending for past)
      if (isAFuture && isBFuture) {
        // Both future: earliest first
        return dateAUTC - dateBUTC;
      } else {
        // Both past: most recent first
        return dateBUTC - dateAUTC;
      }
    });

    // Sort other activities by date (descending - most recent first)
    const sortedOtherActivities = otherActivities.sort((a, b) => {
      // If both have activeFrom dates, sort by date (descending)
      if (a.activeFrom && b.activeFrom) {
        return new Date(b.activeFrom) - new Date(a.activeFrom);
      }
      // If only one has activeFrom, put it first
      if (a.activeFrom && !b.activeFrom) return -1;
      if (!a.activeFrom && b.activeFrom) return 1;

      // If neither has activeFrom, sort by category then name
      if (a.category.name !== b.category.name) {
        return a.category.name.localeCompare(b.category.name);
      }
      return a.name.localeCompare(b.name);
    });

    return {
      yearRoundActivities: sortedYearRoundActivities,
      eventsActivities: sortedEventsActivities,
      otherActivities: sortedOtherActivities
    };
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity);
    setSearchTerm('');

    // Track this activity in recent activities
    updateRecentActivities(activity);

    // Auto-focus on hours input after activity selection
    setTimeout(() => {
      const hoursInput = document.getElementById('hours-input');
      if (hoursInput) hoursInput.focus();
    }, 100);
  };

  const updateRecentActivities = (activity) => {
    setRecentActivities(prev => {
      // Remove activity if it already exists
      const filtered = prev.filter(a => a.id !== activity.id);
      // Add to front, keep max 8 activities
      return [activity, ...filtered].slice(0, 8);
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSuggestionSuccess = (message) => {
    showMessage('success', message);
    setShowSuggestionModal(false);
  };

  // Check if the selected activity requires a description using the new model field
  const isDescriptionRequired = selectedActivity && selectedActivity.requiresDescription;

  const handleSubmit = async () => {
    
    if (!selectedActivity || !hours) {
      showMessage('error', 'Please select an activity and enter hours');
      return;
    }

    if (parseFloat(hours) <= 0 || parseFloat(hours) > 24) {
      showMessage('error', 'Hours must be between 0.25 and 24');
      return;
    }

    // Check if description is required and provided for activities that require it
    if (isDescriptionRequired && (!notes || notes.trim() === '')) {
      const promptText = selectedActivity.descriptionPrompt || 'description';
      showMessage('error', `Please enter ${promptText.toLowerCase()} for ${selectedActivity.name} activities`);
      return;
    }

    setLoading(true);

    try {
      // Use today's date if no date is specified
      const finalEntryDate = entryDate || new Date().toISOString().split('T')[0];

      await apiCall('/api/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          activityId: selectedActivity.id,
          hours: parseFloat(hours),
          entryDate: finalEntryDate,
          notes: notes.trim(),
          schoolId: selectedSchoolId
        })
      });

      // Reset form
      setSelectedActivity(null);
      setHours('');
      setNotes('');
      setEntryDate(''); // Reset to empty date
      
      // Refresh recent entries
      await fetchRecentTimeEntries();
      
      const dateUsed = finalEntryDate === new Date().toISOString().split('T')[0] ? 'today' : `on ${new Date(finalEntryDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}`;
      showMessage('success', `${hours}h logged for ${selectedActivity.name} ${dateUsed}!`);
    } catch (error) {
      showMessage('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) {
      return;
    }

    try {
      await apiCall(`/api/time-entries/${entryId}`, {
        method: 'DELETE'
      });
      
      await fetchRecentTimeEntries();
      showMessage('success', 'Time entry deleted successfully');
    } catch (error) {
      showMessage('error', error.message);
    }
  };

  const getTotalHoursToday = () => {
    const today = new Date().toISOString().split('T')[0];
    return timeEntries
      .filter(entry => entry.entryDate === today)
      .reduce((total, entry) => total + parseFloat(entry.hours), 0)
      .toFixed(1);
  };

  const getRecentActivitiesStats = () => {
    const stats = {};
    timeEntries.forEach(entry => {
      const activityId = entry.activity.id;
      if (!stats[activityId]) {
        stats[activityId] = {
          activity: entry.activity,
          totalHours: 0,
          entryCount: 0
        };
      }
      stats[activityId].totalHours += parseFloat(entry.hours);
      stats[activityId].entryCount += 1;
    });

    return Object.values(stats)
      .map(stat => ({
        ...stat,
        totalHours: Math.round(stat.totalHours * 100) / 100
      }))
      .sort((a, b) => b.totalHours - a.totalHours)
      .slice(0, 5);
  };

  const handleViewPhotos = (activity) => {
    setSelectedActivityForPhotos(activity);
    setView('photos');
  };

  const handlePhotosUpdate = (photoCount) => {
    if (selectedActivityForPhotos) {
      setPhotoStats(prev => ({
        ...prev,
        [selectedActivityForPhotos.id]: photoCount
      }));
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettingsData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleSchoolSelection = (schoolId) => {
    setSettingsData(prev => {
      const currentSchools = prev.schoolIds || [];
      const isSelected = currentSchools.includes(schoolId);

      if (isSelected) {
        // Don't allow deselecting if it's the only school
        if (currentSchools.length === 1) {
          showMessage('error', 'You must be associated with at least one school');
          return prev;
        }
        return {
          ...prev,
          schoolIds: currentSchools.filter(id => id !== schoolId)
        };
      } else {
        return {
          ...prev,
          schoolIds: [...currentSchools, schoolId]
        };
      }
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    if (!settingsData.schoolIds || settingsData.schoolIds.length === 0) {
      showMessage('error', 'Please select at least one school');
      return;
    }

    setSettingsLoading(true);

    try {
      const updateData = {
        firstName: settingsData.firstName,
        lastName: settingsData.lastName,
        email: settingsData.email,
        schoolIds: settingsData.schoolIds
      };

      const updatedUser = await apiCall('/api/profile', {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      showMessage('success', 'Profile updated successfully');

      // Refresh the page to update user data and schools
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      showMessage('error', error.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!settingsData.currentPassword) {
      showMessage('error', 'Current password is required');
      return;
    }
    
    if (!settingsData.newPassword || settingsData.newPassword.length < 6) {
      showMessage('error', 'New password must be at least 6 characters');
      return;
    }
    
    if (settingsData.newPassword !== settingsData.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    setSettingsLoading(true);

    try {
      await apiCall('/api/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: settingsData.currentPassword,
          newPassword: settingsData.newPassword
        })
      });

      showMessage('success', 'Password changed successfully');
      
      // Clear password fields
      setSettingsData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
    } catch (error) {
      showMessage('error', error.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleEditTimeEntry = (entry) => {
    setEditingTimeEntry(entry);
    setShowEditModal(true);
  };
  
  // Add this function to handle successful edit
  const handleEditSuccess = async (message) => {
    showMessage('success', message);
    setShowEditModal(false);
    setEditingTimeEntry(null);
    await fetchRecentTimeEntries();
  };

  // Add this component to your TimeTracker.js file (before the main TimeTracker component)

const TimeEntryEditModal = ({ isOpen, onClose, entry, activities, token, onSuccess, selectedSchoolId }) => {
  const [formData, setFormData] = useState({
    activityId: '',
    hours: '',
    entryDate: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (entry && isOpen) {
      setFormData({
        activityId: entry.activityId || entry.activity?.id || '',
        hours: entry.hours?.toString() || '',
        entryDate: entry.entryDate ? entry.entryDate.split('T')[0] : '',
        notes: entry.notes || ''
      });
      setError('');
    }
  }, [entry, isOpen]);

  const apiCall = async (url, options = {}) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  };

  const selectedActivity = activities.find(a => a.id === formData.activityId);
  const isDescriptionRequired = selectedActivity?.requiresDescription;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.activityId || !formData.hours || !formData.entryDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(formData.hours) <= 0 || parseFloat(formData.hours) > 24) {
      setError('Hours must be between 0.25 and 24');
      return;
    }

    if (isDescriptionRequired && (!formData.notes || formData.notes.trim() === '')) {
      const promptText = selectedActivity.descriptionPrompt || 'description';
      setError(`Please enter ${promptText.toLowerCase()} for ${selectedActivity.name} activities`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiCall(`/api/time-entries/${entry.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          activityId: formData.activityId,
          hours: parseFloat(formData.hours),
          entryDate: formData.entryDate,
          notes: formData.notes.trim(),
          schoolId: selectedSchoolId
        })
      });

      onSuccess(response.message || 'Time entry updated successfully');
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center">
            <Edit3 className="w-5 h-5 mr-2 text-blue-600" />
            Edit Time Entry
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Activity Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity *
            </label>
            <select
              value={formData.activityId}
              onChange={(e) => handleInputChange('activityId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={loading}
            >
              <option value="">Select an activity</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.name} ({activity.category?.name})
                </option>
              ))}
            </select>
          </div>

          {/* Hours Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hours Worked *
            </label>
            <input
              type="number"
              min="0.25"
              max="24"
              step="0.25"
              value={formData.hours}
              onChange={(e) => handleInputChange('hours', e.target.value)}
              placeholder="e.g., 4.5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={loading}
            />
          </div>

          {/* Date Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <input
              type="date"
              value={formData.entryDate}
              onChange={(e) => handleInputChange('entryDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              max={new Date().toISOString().split('T')[0]}
              required
              disabled={loading}
            />
          </div>

          {/* Notes Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isDescriptionRequired ? (
                <span className="flex items-center">
                  <span className="text-red-600">*</span>
                  <span className="ml-1">
                    {selectedActivity?.descriptionPrompt || 'Description'} (Required for {selectedActivity?.name})
                  </span>
                </span>
              ) : (
                'Notes (Optional)'
              )}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder={
                isDescriptionRequired 
                  ? (selectedActivity?.descriptionPrompt || `Please enter required information for ${selectedActivity?.name}`)
                  : "Add any additional details..."
              }
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                isDescriptionRequired 
                  ? 'border-orange-300 bg-orange-50' 
                  : 'border-gray-300'
              }`}
              maxLength={500}
              required={isDescriptionRequired}
              disabled={loading}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">{formData.notes.length}/500 characters</p>
              {isDescriptionRequired && (
                <p className="text-xs text-orange-600 font-medium">
                  {selectedActivity?.descriptionPrompt ? 
                    `${selectedActivity.descriptionPrompt} required` :
                    `Description required for ${selectedActivity?.name} activities`
                  }
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2"
              disabled={loading || (isDescriptionRequired && (!formData.notes || formData.notes.trim() === ''))}
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              )}
              <Save className="w-4 h-4" />
              <span>{loading ? 'Updating...' : 'Update Entry'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

  // Add this to TimeTracker.js - Suggestion Modal Component
  const ActivitySuggestionModal = ({ isOpen, onClose, token, onSuccess, schools, selectedSchoolId }) => {
    const [view, setView] = useState('status'); // 'status' or 'create'
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      categoryId: '',
      reason: '',
      isYearLong: false,
      activeFrom: '',
      activeUntil: '',
      schoolId: selectedSchoolId || ''
    });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Status view state
    const [suggestions, setSuggestions] = useState([]);
    const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
    const [statusFilter, setStatusFilter] = useState('all');
    const [statusLoading, setStatusLoading] = useState(false);
  
    const apiCall = async (url, options = {}) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }
  
      return response.json();
    };
  
    useEffect(() => {
      if (isOpen) {
        // Initialize form with selected school when modal opens
        setFormData(prev => ({
          ...prev,
          schoolId: selectedSchoolId || (schools.length > 0 ? schools[0].id : '')
        }));
        fetchCategories(selectedSchoolId || (schools.length > 0 ? schools[0].id : ''));
        fetchMySuggestions();
      }
    }, [isOpen, selectedSchoolId]);

    useEffect(() => {
      if (isOpen && view === 'status') {
        fetchMySuggestions();
      }
    }, [statusFilter, view]);

    // Fetch categories when school selection changes
    useEffect(() => {
      if (isOpen && formData.schoolId) {
        fetchCategories(formData.schoolId);
        // Reset category selection when school changes
        setFormData(prev => ({ ...prev, categoryId: '' }));
      }
    }, [formData.schoolId]);
  
    const fetchCategories = async (schoolId) => {
      try {
        if (!schoolId) {
          setCategories([]);
          return;
        }
        const activitiesData = await apiCall(`/api/activities?schoolId=${schoolId}`);
        const uniqueCategories = activitiesData.reduce((acc, activity) => {
          if (!acc.find(cat => cat.id === activity.category.id)) {
            acc.push(activity.category);
          }
          return acc;
        }, []);
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
  
    const fetchMySuggestions = async () => {
      try {
        setStatusLoading(true);
        const params = new URLSearchParams();
        if (statusFilter !== 'all') {
          params.append('status', statusFilter);
        }
        
        const data = await apiCall(`/api/my-activity-suggestions?${params}`);
        setSuggestions(data.suggestions || []);
        setStatusCounts(data.statusCounts || { pending: 0, approved: 0, rejected: 0, total: 0 });
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setError('Failed to load your suggestions');
      } finally {
        setStatusLoading(false);
      }
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');
  
      try {
        const submitData = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          categoryId: formData.categoryId || null,
          reason: formData.reason.trim() || null,
          isYearLong: formData.isYearLong,
          activeFrom: formData.isYearLong ? null : (formData.activeFrom || null),
          activeUntil: formData.isYearLong ? null : (formData.activeUntil || null)
        };
  
        await apiCall('/api/activity-suggestions', {
          method: 'POST',
          body: JSON.stringify(submitData)
        });
  
        onSuccess('Activity suggestion submitted successfully! You can check its status anytime.');
        handleClose();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    const handleClose = () => {
      setView('status');
      setFormData({
        name: '',
        description: '',
        categoryId: '',
        reason: '',
        isYearLong: false,
        activeFrom: '',
        activeUntil: '',
        schoolId: selectedSchoolId || (schools.length > 0 ? schools[0].id : '')
      });
      setError('');
      setStatusFilter('all');
      onClose();
    };
  
    const switchToCreateView = () => {
      setView('create');
      setError('');
    };
  
    const switchToStatusView = () => {
      setView('status');
      setError('');
      fetchMySuggestions();
    };
  
    if (!isOpen) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center">
              <Plus className="w-6 h-6 mr-2 text-blue-600" />
              {view === 'status' ? 'Activity Suggestions' : 'Suggest New Activity'}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
  
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 px-6">
            <button
              onClick={switchToStatusView}
              className={`flex items-center px-4 py-3 border-b-2 font-medium text-sm ${
                view === 'status'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Eye className="w-4 h-4 mr-2" />
              My Suggestions
              {statusCounts.total > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-800 text-xs rounded-full px-2 py-1">
                  {statusCounts.total}
                </span>
              )}
            </button>
            <button
              onClick={switchToCreateView}
              className={`flex items-center px-4 py-3 border-b-2 font-medium text-sm ${
                view === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Suggestion
            </button>
          </div>
  
          {error && (
            <div className="mx-6 mt-4 bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">Error:</div>
                <div className="mt-1">{error}</div>
              </div>
            </div>
          )}
  
          {/* Status View */}
          {view === 'status' && (
            <div className="p-6">
              {/* Status Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-700">{statusCounts.total}</div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-700">{statusCounts.pending}</div>
                  <div className="text-sm text-yellow-600">Pending</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{statusCounts.approved}</div>
                  <div className="text-sm text-green-600">Approved</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{statusCounts.rejected}</div>
                  <div className="text-sm text-red-600">Rejected</div>
                </div>
              </div>
  
              {/* Status Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={statusLoading}
                >
                  <option value="all">All Suggestions ({statusCounts.total})</option>
                  <option value="pending">Pending ({statusCounts.pending})</option>
                  <option value="approved">Approved ({statusCounts.approved})</option>
                  <option value="rejected">Rejected ({statusCounts.rejected})</option>
                </select>
              </div>
  
              {/* Suggestions List */}
              {statusLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
                </div>
              ) : suggestions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Plus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-base mb-2">
                    {statusFilter === 'all' 
                      ? "You haven't suggested any activities yet" 
                      : `No ${statusFilter} suggestions`
                    }
                  </p>
                  <p className="text-sm">Click "New Suggestion" to suggest your first activity!</p>
                </div>
              ) : (
                <div className="space-y-4">
{suggestions.map((suggestion) => (
  <div key={suggestion.id} className="border border-gray-200 rounded-lg p-4">
    <div className="flex items-start justify-between mb-2">
      <div className="flex-1">
        <div className="flex items-center space-x-2 mb-1">
          <h4 className="font-medium text-gray-800">{suggestion.name}</h4>
          <div className={`px-2 py-1 rounded text-xs font-medium ${
            suggestion.status === 'pending' 
              ? 'bg-yellow-100 text-yellow-700' 
              : suggestion.status === 'approved'
              ? 'bg-green-100 text-green-700'
              : suggestion.status === 'deleted'
              ? 'bg-gray-100 text-gray-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {suggestion.status}
          </div>
          {suggestion.isYearLong && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
              Year-Long
            </span>
          )}
        </div>
        
        <div className="text-sm text-gray-600 space-y-1">
          <p><strong>Submitted:</strong> {new Date(suggestion.createdAt).toLocaleDateString()}</p>
          {suggestion.category && (
            <p><strong>Category:</strong> {suggestion.category.name}</p>
          )}
          {!suggestion.isYearLong && (suggestion.activeFrom || suggestion.activeUntil) && (
            <p><strong>Dates:</strong> 
              {suggestion.activeFrom && ` From ${new Date(suggestion.activeFrom).toLocaleDateString()}`}
              {suggestion.activeUntil && ` Until ${new Date(suggestion.activeUntil).toLocaleDateString()}`}
            </p>
          )}
        </div>

        {suggestion.description && (
          <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
            <strong>Description:</strong> {suggestion.description}
          </div>
        )}

        {suggestion.reason && (
          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
            <strong>Your reason:</strong> {suggestion.reason}
          </div>
        )}

        {/* ADMIN FEEDBACK SECTION */}
        {suggestion.adminReason && (
          <div className={`mt-2 p-3 rounded border ${
            suggestion.status === 'rejected' 
              ? 'bg-red-50 border-red-200'
              : suggestion.status === 'deleted'
              ? 'bg-gray-50 border-gray-200'
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 mt-0.5">
                {suggestion.status === 'rejected' ? (
                  <X className="w-4 h-4 text-red-500" />
                ) : suggestion.status === 'deleted' ? (
                  <Trash2 className="w-4 h-4 text-gray-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                )}
              </div>
              <div className="text-sm">
                <p className={`font-medium ${
                  suggestion.status === 'rejected' 
                    ? 'text-red-700'
                    : suggestion.status === 'deleted'
                    ? 'text-gray-700'
                    : 'text-yellow-700'
                }`}>
                  Administrator feedback:
                </p>
                <p className={`mt-1 ${
                  suggestion.status === 'rejected' 
                    ? 'text-red-600'
                    : suggestion.status === 'deleted'
                    ? 'text-gray-600'
                    : 'text-yellow-600'
                }`}>
                  {suggestion.adminReason}
                </p>
              </div>
            </div>
          </div>
        )}

        {suggestion.status === 'approved' && suggestion.approvedActivity && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
            <strong>Good news!</strong> Your suggestion has been approved and is now available as an activity: 
            <span className="font-medium"> "{suggestion.approvedActivity.name}"</span>
            {suggestion.approvedActivity.isActive && (
              <span className="text-green-600"> (Currently active for time logging)</span>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
))}
                </div>
              )}
            </div>
          )}
  
          {/* Create View */}
          {view === 'create' && (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School *
                </label>
                <select
                  required
                  value={formData.schoolId}
                  onChange={(e) => setFormData({ ...formData, schoolId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="">Select a school</option>
                  {schools && schools.map(school => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Library Book Fair Setup"
                  disabled={loading}
                  maxLength={100}
                />
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category (Optional)
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="">Select a category (optional)</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Brief description of what this activity involves..."
                  disabled={loading}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 mt-1">{formData.description.length}/200 characters</p>
              </div>
  
              {/* Year-Long Activity Option */}
              <div className="border-t border-gray-200 pt-4">
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isYearLong}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isYearLong: e.target.checked,
                          activeFrom: e.target.checked ? '' : formData.activeFrom,
                          activeUntil: e.target.checked ? '' : formData.activeUntil
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={loading}
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        This is a year-long activity
                      </span>
                    </label>
                  </div>
  
                  {formData.isYearLong && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-700">
                        This activity will be available throughout the year. Date fields are disabled for year-long activities.
                      </p>
                    </div>
                  )}
                </div>
              </div>
  
              {/* Date Fields */}
              {!formData.isYearLong && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={formData.activeFrom}
                      onChange={(e) => setFormData({ ...formData, activeFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={formData.activeUntil}
                      onChange={(e) => setFormData({ ...formData, activeUntil: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      disabled={loading || !formData.activeFrom}
                      min={formData.activeFrom}
                    />
                  </div>
                </div>
              )}
  
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Why is this activity needed? (Optional)
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Help administrators understand why this activity should be added..."
                  disabled={loading}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">{formData.reason.length}/500 characters</p>
              </div>
  
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={switchToStatusView}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Back to Status
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2"
                  disabled={loading || !formData.name.trim()}
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                  <Plus className="w-4 h-4" />
                  <span>{loading ? 'Submitting...' : 'Submit Suggestion'}</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

  // Component to render activity button
  // Updated ActivityButton component to handle year-round activities
  const ActivityButton = ({ activity, isYearRound = false, isEvent = false }) => (
    <button
      type="button"
      onClick={() => handleActivitySelect(activity)}
      className="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-blue-50 transition-colors relative group"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <p className="font-medium text-gray-800">{activity.name}</p>
            {isYearRound && !activity.activeFrom && activity.category.name.toLowerCase() !== 'classroom' && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Ongoing</span>
            )}
          </div>
          
          {!isYearRound && !isEvent && (
            <p className="text-sm text-gray-500">{activity.category.name}</p>
          )}
          
          {/* Show category for year-round activities that aren't classroom */}
          {isYearRound && activity.category.name.toLowerCase() !== 'classroom' && (
            <p className="text-sm text-gray-500">{activity.category.name}</p>
          )}
          
          {/* Show Event Date for Events activities */}
          {isEvent && activity.activeFrom && (
            <div className="flex items-center space-x-1 mt-1">
              <Calendar className="w-3 h-3 text-blue-500" />
              <p className="text-sm text-blue-600 font-medium">
                Event Date: {new Date(activity.activeFrom).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                {(() => {
                  // Parse event date in UTC
                  const eventDate = new Date(activity.activeFrom);
                  const eventDateUTC = new Date(Date.UTC(
                    eventDate.getUTCFullYear(),
                    eventDate.getUTCMonth(),
                    eventDate.getUTCDate()
                  ));

                  // Get today's date in UTC
                  const now = new Date();
                  const todayUTC = new Date(Date.UTC(
                    now.getUTCFullYear(),
                    now.getUTCMonth(),
                    now.getUTCDate()
                  ));

                  const isToday = eventDateUTC.getTime() === todayUTC.getTime();
                  const isFuture = eventDateUTC > todayUTC;
                  const isPast = eventDateUTC < todayUTC;

                  if (isToday) {
                    return <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Today</span>;
                  } else if (isFuture) {
                    return <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Upcoming</span>;
                  } else if (isPast) {
                    return <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Past</span>;
                  }
                  return null;
                })()}
              </p>
            </div>
          )}
          
          {/* Show regular start date for other activities (not year-round or events) */}
          {!isYearRound && !isEvent && activity.activeFrom && (
            <div className="flex items-center space-x-1 mt-1">
              <Calendar className="w-3 h-3 text-gray-400" />
              <p className="text-xs text-gray-400">
                {new Date(activity.activeFrom).toLocaleDateString('en-US', { timeZone: 'UTC' })}
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {photoStats[activity.id] > 0 && (
            <div className="flex items-center space-x-1">
              <Camera className="w-3 h-3 text-blue-400" />
              <span className="text-xs text-blue-400">
                {photoStats[activity.id]}
              </span>
            </div>
          )}
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: activity.category.color }}
          ></div>
        </div>
      </div>
    </button>
  );

  const { yearRoundActivities, eventsActivities, otherActivities } = getActivityLists();

  // School Selector Component
  const SchoolSelector = () => {
    if (!schools || schools.length === 0) {
      return <span>Loading schools...</span>;
    }

    // For single school, just show the name as text
    if (schools.length === 1) {
      return <span>{selectedSchool ? selectedSchool.name : schools[0].name}</span>;
    }

    // For multiple schools, show dropdown
    return (
      <div className="relative min-w-0">
        <select
          value={selectedSchoolId || ''}
          onChange={(e) => selectSchool(e.target.value)}
          className="appearance-none bg-transparent border-0 border-b border-gray-300 px-1 py-0 pr-5 text-xs sm:text-sm md:text-base font-normal text-gray-600 hover:border-blue-400 focus:outline-none focus:ring-0 focus:border-blue-500 cursor-pointer"
        >
          {schools.map(school => (
            <option key={school.id} value={school.id}>
              {school.name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <RichmondStreetSchoolLogo
                size={72}
                className="flex-shrink-0 w-16 h-16 sm:w-24 sm:h-24"
                logoUrl={selectedSchool?.logoUrl || "/assets/school-logo.png"}
              />
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-lg md:text-2xl lg:text-3xl font-bold text-gray-800 mb-1 leading-tight">
                  Volunteer Time Tracker
                </h1>
                <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm md:text-base text-gray-600 leading-tight">
                  <SchoolSelector />
                  <span>•</span>
                  <span>{user?.firstName}</span>
                </div>
              </div>
            </div>

            {/* Desktop Menu - Hidden on mobile */}
            <div className="hidden md:flex items-center space-x-2 lg:space-x-4 flex-shrink-0">
              <button
                onClick={() => setShowSuggestionModal(true)}
                className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-50 flex items-center space-x-1"
                title="Suggest New Activity"
              >
                <Plus className="w-5 h-5 lg:w-6 lg:h-6" />
                <span className="text-sm font-medium">Suggest Activity</span>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="text-gray-600 hover:text-blue-600 p-2 rounded-lg hover:bg-blue-50 flex items-center space-x-1"
                title="Account Settings"
              >
                <Settings className="w-5 h-5 lg:w-6 lg:h-6" />
                <span className="text-sm font-medium">Settings</span>
              </button>

              {user?.role === 'admin' && (
                <button
                  onClick={() => onViewChange('admin')}
                  className="text-purple-600 hover:text-purple-800 p-2 rounded-lg hover:bg-purple-50 flex items-center space-x-1"
                  title="Admin Panel"
                >
                  <Shield className="w-5 h-5 lg:w-6 lg:h-6" />
                  <span className="text-sm font-medium">Admin</span>
                </button>
              )}
              <button
                onClick={onLogout}
                className="text-gray-500 hover:text-red-500 p-2 rounded-lg hover:bg-red-50"
                title="Logout"
              >
                <LogOut className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden relative">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                title="Menu"
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Mobile Dropdown Menu */}
              {showMobileMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMobileMenu(false)}
                  ></div>

                  {/* Menu */}
                  <div className="absolute right-0 top-12 z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px]">
                    <button
                      onClick={() => {
                        setShowSuggestionModal(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 text-gray-700"
                    >
                      <Plus className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Suggest Activity</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowSettings(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 text-gray-700"
                    >
                      <Settings className="w-5 h-5 text-gray-600" />
                      <span className="font-medium">Settings</span>
                    </button>

                    {user?.role === 'admin' && (
                      <button
                        onClick={() => {
                          onViewChange('admin');
                          setShowMobileMenu(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 text-gray-700"
                      >
                        <Shield className="w-5 h-5 text-purple-600" />
                        <span className="font-medium">Admin Panel</span>
                      </button>
                    )}

                    <div className="border-t border-gray-200 my-2"></div>

                    <button
                      onClick={() => {
                        onLogout();
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 text-red-600"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* View Toggle - Mobile Responsive */}
          <div className="flex border-b border-gray-200 mt-4 sm:mt-6 overflow-x-auto">
            <button
              onClick={() => setView('activities')}
              className={`flex items-center px-2 sm:px-4 py-2 mr-2 sm:mr-4 border-b-2 font-medium text-sm sm:text-base whitespace-nowrap ${
                view === 'activities'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Log Time</span>
              <span className="xs:hidden">Log</span>
            </button>
            
            <button
              onClick={() => setView('recent')}
              className={`flex items-center px-2 sm:px-4 py-2 mr-2 sm:mr-4 border-b-2 font-medium text-sm sm:text-base whitespace-nowrap ${
                view === 'recent'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Recent Activity</span>
              <span className="xs:hidden">Recent</span>
            </button>
            
            <button
              onClick={() => setView('photos')}
              className={`flex items-center px-2 sm:px-4 py-2 border-b-2 font-medium text-sm sm:text-base whitespace-nowrap ${
                view === 'photos'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Camera className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Manage Photos</span>
              <span className="xs:hidden">Photos</span>
            </button>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`rounded-lg p-4 mb-6 flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Photos View */}
        {view === 'photos' ? (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Photo Management</h2>
                <p className="text-gray-600">
                  {selectedActivityForPhotos 
                    ? `Managing photos for ${selectedActivityForPhotos.name}`
                    : 'Select an activity to manage photos'
                  }
                </p>
              </div>
              <button
                onClick={() => setView('activities')}
                className="text-blue-600 hover:text-blue-800 px-4 py-2 rounded-lg hover:bg-blue-50"
              >
                Back to Activities
              </button>
            </div>

            {!selectedActivityForPhotos ? (
              /* Activity Selection for Photos */
              <div className="space-y-6">
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Ready to Manage Photos?</h3>
                    <p className="text-gray-600">View all your photos, or select an activity to view or add photos.</p>
                  </div>
                  <button
                    onClick={() => setSelectedActivityForPhotos({ id: 'all', name: 'All Activities' })}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2 mx-auto"
                  >
                    <Image className="w-5 h-5" />
                    <span>View All Photos</span>
                  </button>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Activity
                  </label>
                  <div className="relative mb-4">
                    <input
                      type="text"
                      placeholder="Search activities..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                    {[...yearRoundActivities, ...eventsActivities, ...otherActivities].map((activity) => (
                      <button
                        key={activity.id}
                        onClick={() => setSelectedActivityForPhotos(activity)}
                        className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: activity.category.color }}
                            ></div>
                            <div className="flex-1">
                              <h3 className="font-medium text-gray-800">{activity.name}</h3>
                              <div className="flex items-center space-x-2">
                                <p className="text-sm text-gray-500">{activity.category.name}</p>
                                {!activity.activeFrom && activity.category.name.toLowerCase() !== 'classroom' && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Ongoing</span>
                                )}
                                {activity.category.name.toLowerCase() === 'events' && activity.activeFrom && (
                                  <span className="text-xs text-blue-600">
                                    {new Date(activity.activeFrom).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end space-y-1">
                            {photoStats[activity.id] > 0 ? (
                              <div className="flex items-center space-x-1">
                                <Camera className="w-3 h-3 text-blue-500" />
                                <span className="text-xs text-blue-600 font-medium">
                                  {photoStats[activity.id]} photos
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                <Camera className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-400">No photos</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {[...yearRoundActivities, ...eventsActivities, ...otherActivities].length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No activities found</p>
                      <p className="text-sm">Try adjusting your search terms</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Enhanced Photo Manager with Activity Header */
              <div className="space-y-6">
                {/* Activity Header */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {selectedActivityForPhotos.id !== 'all' && (
                        <div
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: selectedActivityForPhotos.category.color }}
                        ></div>
                      )}
                      {selectedActivityForPhotos.id === 'all' && (
                        <Image className="w-6 h-6 text-blue-600" />
                      )}
                      <div>
                        <h3 className="font-medium text-gray-800">{selectedActivityForPhotos.name}</h3>
                        {selectedActivityForPhotos.id !== 'all' && (
                          <p className="text-sm text-gray-500">{selectedActivityForPhotos.category.name}</p>
                        )}
                        {selectedActivityForPhotos.id === 'all' && (
                          <p className="text-sm text-gray-500">Viewing photos from all activities</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {selectedActivityForPhotos.id !== 'all' && (
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {photoStats[selectedActivityForPhotos.id] || 0} photos
                          </p>
                        </div>
                      )}
                      <button
                        onClick={() => setSelectedActivityForPhotos(null)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 rounded hover:bg-blue-100"
                      >
                        {selectedActivityForPhotos.id === 'all' ? 'Back to Selection' : 'Change Activity'}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Photo Manager Component */}
                <PhotoManager
                  activityId={selectedActivityForPhotos.id === 'all' ? null : selectedActivityForPhotos.id}
                  token={token}
                  isAdmin={user?.role === 'admin'}
                  onPhotosUpdate={handlePhotosUpdate}
                  schoolId={selectedSchoolId}
                  viewAllPhotos={selectedActivityForPhotos.id === 'all'}
                />
              </div>
            )}
          </div>
        ) : view === 'activities' ? (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Activity Selection & Time Entry */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <Plus className="w-5 h-5 mr-2 text-blue-500" />
                Quick Time Entry
              </h2>

              <div className="space-y-4">
                {/* Activity Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search className="w-4 h-4 inline mr-1" />
                    Select Activity
                  </label>
                  
                  {selectedActivity ? (
                    <div className="border border-green-300 bg-green-50 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div 
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: selectedActivity.category.color }}
                          ></div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{selectedActivity.name}</p>
                            <div className="flex items-center space-x-3">
                              <p className="text-sm text-gray-500">{selectedActivity.category.name}</p>
                              {selectedActivity.category.name.toLowerCase() !== 'classroom' && selectedActivity.activeFrom && (
                                <div className="flex items-center space-x-1">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <p className="text-xs text-gray-400">
                                    {new Date(selectedActivity.activeFrom).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                                  </p>
                                </div>
                              )}
                              {photoStats[selectedActivity.id] > 0 && (
                                <div className="flex items-center space-x-1">
                                  <Camera className="w-3 h-3 text-blue-400" />
                                  <p className="text-xs text-blue-400">
                                    {photoStats[selectedActivity.id]} photos
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleViewPhotos(selectedActivity)}
                            className="flex items-center space-x-1 px-2 py-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded text-sm"
                            title="Add photos to this activity"
                          >
                            <Camera className="w-4 h-4" />
                            <span>Photos</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedActivity(null)}
                            className="text-red-500 hover:text-red-700 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="relative mb-2">
                        <input
                          type="text"
                          placeholder="Search activities..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                      </div>

                      <div className="space-y-2">

                        {/* Events Activities Section */}
                        {eventsActivities.length > 0 && (
                          <div className="border border-gray-300 rounded-lg overflow-visible">
                            <button
                              onClick={() => toggleSection('events')}
                              className="w-full bg-blue-50 hover:bg-blue-100 px-4 py-3 flex items-center justify-between transition-colors rounded-t-lg"
                            >
                              <div className="flex items-center space-x-2">
                                {expandedSections.events ? (
                                  <ChevronDown className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-blue-600" />
                                )}
                                <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
                                  Events
                                </h3>
                                <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs rounded-full">
                                  {eventsActivities.length}
                                </span>
                              </div>
                              <div className="flex items-center text-xs text-blue-600">
                                <svg
                                  className="w-3 h-3 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="M9,9h0a3,3,0,0,1,6,0c0,2-3,3-3,3"></path>
                                  <path d="M12,17h0"></path>
                                </svg>
                                Includes planning time
                              </div>
                            </button>
                            {expandedSections.events && (
                              <div className="max-h-80 overflow-y-auto overflow-x-visible">
                                {eventsActivities.map((activity) => (
                                  <ActivityButton key={activity.id} activity={activity} isEvent={true} />
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Year-Round Activities Section */}
                        {yearRoundActivities.length > 0 && (
                          <div className="border border-gray-300 rounded-lg overflow-visible">
                            <button
                              onClick={() => toggleSection('yearRound')}
                              className="w-full bg-green-50 hover:bg-green-100 px-4 py-3 flex items-center justify-between transition-colors rounded-t-lg"
                            >
                              <div className="flex items-center space-x-2">
                                {expandedSections.yearRound ? (
                                  <ChevronDown className="w-4 h-4 text-green-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-green-600" />
                                )}
                                <h3 className="text-sm font-semibold text-green-700 uppercase tracking-wide">
                                  Year-Round Activities
                                </h3>
                                <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs rounded-full">
                                  {yearRoundActivities.length}
                                </span>
                              </div>
                            </button>
                            {expandedSections.yearRound && (
                              <div className="max-h-80 overflow-y-auto overflow-x-visible">
                                {yearRoundActivities.map((activity) => (
                                  <ActivityButton key={activity.id} activity={activity} isYearRound={true} />
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Other Activities Section */}
                        {otherActivities.length > 0 && (
                          <div className="border border-gray-300 rounded-lg overflow-visible">
                            <button
                              onClick={() => toggleSection('classroom')}
                              className="w-full bg-gray-50 hover:bg-gray-100 px-4 py-3 flex items-center justify-between transition-colors rounded-t-lg"
                            >
                              <div className="flex items-center space-x-2">
                                {expandedSections.classroom ? (
                                  <ChevronDown className="w-4 h-4 text-gray-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-600" />
                                )}
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                  Other Activities
                                </h3>
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-800 text-xs rounded-full">
                                  {otherActivities.length}
                                </span>
                              </div>
                            </button>
                            {expandedSections.classroom && (
                              <div className="max-h-80 overflow-y-auto overflow-x-visible">
                                {otherActivities.map((activity) => (
                                  <ActivityButton key={activity.id} activity={activity} isYearRound={false} />
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {yearRoundActivities.length === 0 && eventsActivities.length === 0 && otherActivities.length === 0 && (
                          <div className="p-8 text-center text-gray-500 border border-gray-300 rounded-lg">
                            {searchTerm ? 'No activities found' : 'Start typing to search activities'}
                          </div>
                        )}
                      </div>

                    </>
                  )}
                </div>

                {/* Hours Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Hours Worked
                  </label>
                  <input
                    id="hours-input"
                    type="number"
                    min="0.25"
                    max="24"
                    step="0.25"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="e.g., 4.5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={!selectedActivity}
                  />
                </div>

                {/* Date Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Date (Optional)
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="date"
                      value={entryDate}
                      onChange={(e) => setEntryDate(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      max={new Date().toISOString().split('T')[0]}
                    />
                    <button
                      type="button"
                      onClick={() => setEntryDate(new Date().toISOString().split('T')[0])}
                      className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      Today
                    </button>
                    {entryDate && (
                      <button
                        type="button"
                        onClick={() => setEntryDate('')}
                        className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {entryDate 
                      ? `Logging time for ${new Date(entryDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}`
                      : `Will default to today (${new Date().toLocaleDateString()}) if left blank`
                    }
                  </p>
                </div>

                {/* Notes - Enhanced for configurable requirement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isDescriptionRequired ? (
                      <span className="flex items-center">
                        <span className="text-red-600">*</span>
                        <span className="ml-1">
                          {selectedActivity.descriptionPrompt || 'Description'} (Required for {selectedActivity.name})
                        </span>
                      </span>
                    ) : (
                      'Notes (Optional)'
                    )}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={
                      isDescriptionRequired 
                        ? (selectedActivity.descriptionPrompt || `Please enter required information for ${selectedActivity.name}`)
                        : "Add any additional details..."
                    }
                    rows={3}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                      isDescriptionRequired 
                        ? 'border-orange-300 bg-orange-50' 
                        : 'border-gray-300'
                    }`}
                    maxLength={500}
                    required={isDescriptionRequired}
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">{notes.length}/500 characters</p>
                    {isDescriptionRequired && (
                      <p className="text-xs text-orange-600 font-medium">
                        {selectedActivity.descriptionPrompt ? 
                          `${selectedActivity.descriptionPrompt} required` :
                          `Description required for ${selectedActivity.name} activities`
                        }
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={loading || !selectedActivity || !hours || (isDescriptionRequired && (!notes || notes.trim() === ''))}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{loading ? 'Logging Time...' : 'Log Time'}</span>
                </button>
              </div>
            </div>

            {/* Recent Activity Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              {user.role === 'admin' ? 'Most Active Tasks' : 'Your Most Active Tasks'}
            </h2>
            
            {getRecentActivitiesStats().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No time entries yet</p>
                <p className="text-sm">Start logging time to see your activity stats</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getRecentActivitiesStats().map((stat) => (
                  <div key={stat.activity.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3 flex-1">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: stat.activity.category.color }}
                        ></div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-800">{stat.activity.name}</h3>
                          <div className="flex items-center space-x-3">
                            <p className="text-sm text-gray-500">{stat.activity.category.name}</p>
                            {stat.activity.category.name.toLowerCase() !== 'classroom' && stat.activity.activeFrom && (
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                <p className="text-xs text-gray-400">
                                  {new Date(stat.activity.activeFrom).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                                </p>
                              </div>
                            )}
                            {photoStats[stat.activity.id] > 0 && (
                              <button
                                onClick={() => handleViewPhotos(stat.activity)}
                                className="flex items-center space-x-1 text-blue-500 hover:text-blue-700 cursor-pointer"
                              >
                                <Camera className="w-3 h-3" />
                                <span className="text-xs">{photoStats[stat.activity.id]}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-blue-600">{stat.totalHours}h</p>
                        <p className="text-xs text-gray-500">{stat.entryCount} entries</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ 
                          width: `${Math.min((stat.totalHours / Math.max(...getRecentActivitiesStats().map(s => s.totalHours))) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                    
                    {/* Show recent entries for this activity with edit buttons */}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-600 mb-2">Recent Entries:</p>
                      {timeEntries
                        .filter(entry => entry.activity.id === stat.activity.id)
                        .slice(0, 3)
                        .map((entry) => (
                          <div key={entry.id} className="flex items-center justify-between text-xs bg-gray-50 rounded p-2">
                            <div className="flex items-center space-x-2">
                              <span>{new Date(entry.entryDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}</span>
                              <span className="font-medium">{entry.hours}h</span>
                              {entry.notes && (
                                <span className="text-gray-500 truncate max-w-24">"{entry.notes}"</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-1 ml-2">
                              <button
                                onClick={() => handleEditTimeEntry(entry)}
                                className="text-blue-500 hover:text-blue-700 p-0.5 rounded hover:bg-blue-100 transition-colors"
                                title="Edit entry"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className="text-red-500 hover:text-red-700 p-0.5 rounded hover:bg-red-100 transition-colors"
                                title="Delete entry"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        ) : (
          /* Recent Entries View */
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Recent Time Entries</h2>
            
            {timeEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No recent entries found</p>
                <p className="text-sm">Start logging time to see your recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {timeEntries.map((entry) => (
                  <div key={entry.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: entry.activity.category.color }}
                          ></div>
                          <div>
                            <h3 className="font-medium text-gray-800">{entry.activity.name}</h3>
                            <p className="text-sm text-gray-500">{entry.activity.category.name}</p>
                          </div>
                          {photoStats[entry.activity.id] > 0 && (
                            <button
                              onClick={() => handleViewPhotos(entry.activity)}
                              className="flex items-center space-x-1 text-blue-500 hover:text-blue-700 cursor-pointer"
                            >
                              <Camera className="w-3 h-3" />
                              <span className="text-xs">{photoStats[entry.activity.id]}</span>
                            </button>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {entry.hours}h
                          </span>
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(entry.entryDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                          </span>
                        </div>
                        
                        {entry.notes && (
                          <p className="text-sm text-gray-600 bg-gray-50 rounded p-2 mt-2">
                            {entry.notes}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-1 ml-4">
                        <button
                          onClick={() => handleEditTimeEntry(entry)}
                          className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="Edit entry"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                  <Settings className="w-6 h-6 mr-2 text-blue-600" />
                  Account Settings
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={settingsLoading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-8">
                {/* Profile Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-gray-600" />
                    Profile Information
                  </h4>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={settingsData.firstName}
                          onChange={(e) => handleSettingsChange('firstName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          disabled={settingsLoading}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={settingsData.lastName}
                          onChange={(e) => handleSettingsChange('lastName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          disabled={settingsLoading}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={settingsData.email}
                          onChange={(e) => handleSettingsChange('email', e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          disabled={settingsLoading}
                          required
                        />
                        <Mail className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      </div>
                    </div>

                    {/* School Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        Associated Schools <span className="text-red-600">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Select which school(s) you volunteer for. You must select at least one.
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3">
                        {availableSchools.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">
                            Loading schools...
                          </div>
                        ) : (
                          availableSchools.map((school) => {
                            const isSelected = settingsData.schoolIds?.includes(school.id);
                            return (
                              <label
                                key={school.id}
                                className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSchoolSelection(school.id)}
                                  disabled={settingsLoading}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="ml-3 text-sm font-medium text-gray-700">
                                  {school.name}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {settingsData.schoolIds?.length > 0 && (
                        <p className="text-xs text-gray-600 mt-2">
                          Selected: {settingsData.schoolIds.length} school{settingsData.schoolIds.length !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={settingsLoading}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-2"
                      >
                        {settingsLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                        <Save className="w-4 h-4" />
                        <span>Update Profile</span>
                      </button>
                    </div>
                  </form>
                </div>

                {/* Change Password */}
                <div className="border-t border-gray-200 pt-8">
                  <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                    <Lock className="w-5 h-5 mr-2 text-gray-600" />
                    Change Password
                  </h4>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={settingsData.currentPassword}
                          onChange={(e) => handleSettingsChange('currentPassword', e.target.value)}
                          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          disabled={settingsLoading}
                          placeholder="Enter your current password"
                        />
                        <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                          disabled={settingsLoading}
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={settingsData.newPassword}
                            onChange={(e) => handleSettingsChange('newPassword', e.target.value)}
                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            disabled={settingsLoading}
                            placeholder="Enter new password"
                          />
                          <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                            disabled={settingsLoading}
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={settingsData.confirmPassword}
                            onChange={(e) => handleSettingsChange('confirmPassword', e.target.value)}
                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            disabled={settingsLoading}
                            placeholder="Confirm new password"
                          />
                          <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                            disabled={settingsLoading}
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={settingsLoading}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-2"
                      >
                        {settingsLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                        <Lock className="w-4 h-4" />
                        <span>Change Password</span>
                      </button>
                    </div>
                  </form>
                </div>

                {/* Account Information */}
                <div className="border-t border-gray-200 pt-8">
                  <h4 className="text-lg font-medium text-gray-800 mb-4">Account Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Account Type:</span>
                      <span className={`text-sm px-2 py-1 rounded ${
                        user?.role === 'admin' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {user?.role === 'admin' ? 'Administrator' : 'User'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Member Since:</span>
                      <span className="text-sm text-gray-700">
                        {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Last Login:</span>
                      <span className="text-sm text-gray-700">
                        {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ActivitySuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => setShowSuggestionModal(false)}
        token={token}
        onSuccess={handleSuggestionSuccess}
        schools={schools}
        selectedSchoolId={selectedSchoolId}
      />

      <TimeEntryEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingTimeEntry(null);
        }}
        entry={editingTimeEntry}
        activities={activities}
        token={token}
        onSuccess={handleEditSuccess}
        selectedSchoolId={selectedSchoolId}
      />
    </div>
  );
};

export default TimeTracker;